import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useTabsContext } from '@cbhq/cds-common/tabs/TabsContext';
import type { TabValue } from '@cbhq/cds-common/tabs/useTabs';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { Box } from '@cbhq/cds-web/layout/Box';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Tooltip } from '@cbhq/cds-web/overlays/tooltip/Tooltip';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { TabLabel } from '@cbhq/cds-web/tabs/TabLabel';
import type { TabComponent, TabComponentProps } from '@cbhq/cds-web/tabs/Tabs';
import { Tabs, TabsActiveIndicator } from '@cbhq/cds-web/tabs/Tabs';
import { Text } from '@cbhq/cds-web/typography/Text';

import { CountOrSingleCell } from '../../../components/CountOrSingleCell';

// A disabled tab with a custom Component acts as a visual separator between
// the "All" tab and the category tabs. CDS Tabs has no first-class separator
// slot, so this is the least-invasive workaround. The tab is marked disabled
// so it is excluded from click handling; keyboard navigation skips disabled
// items in CDS Tabs, so it should not receive focus.
const DIVIDER_ID = 'tab-divider';

type TeamTab = { id: string; label?: string; count: number };

// Extends TabComponentProps with a count so TabLabel can render it natively.
// Since count is optional, TabComponentProps<string> is still assignable to
// CountedTabValue, keeping the cast at the TabComponent call site safe.
type CountedTabValue = TabComponentProps<string> & { count?: number };

type TeamHeaderProps = {
  orgName: string;
  orgLogoSrc: string;
  memberCount: number;
  entities: string[];
  allTab?: TeamTab;
  categoryTabs: TeamTab[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  onInvite?: () => void;
  isInviteDisabled?: boolean;
};

const TAB_DIVIDER_STYLE = { height: '50%', width: 1 };
const LOGO_BOX_STYLE = { overflow: 'hidden', flexShrink: 0 };

const messages = defineMessages({
  memberCount: {
    id: 'teamHeader.memberCount',
    defaultMessage: '{count, plural, one {# team member} other {# team members}}',
    description: 'Member count shown below the org name',
  },
  entities: {
    id: 'teamHeader.entities',
    defaultMessage: '{count, plural, one {# entity} other {# entities}}',
    description: 'Tooltip trigger showing how many entities the org has',
  },
  inviteTeamMember: {
    id: 'teamHeader.inviteTeamMember',
    defaultMessage: 'Invite team member',
    description: 'Button label to open the invite team member flow',
  },
  orgLogoAlt: {
    id: 'teamHeader.orgLogoAlt',
    defaultMessage: '{orgName} logo',
    description: 'Alt text for the organization logo image',
  },
  inviteDisabledTooltip: {
    id: 'teamHeader.inviteDisabledTooltip',
    defaultMessage:
      'You are not allowed to invite members. Talk to your organization administrator for access.',
    description: 'Tooltip shown on the disabled invite button when the user cannot manage members',
  },
});

function TabDivider() {
  return (
    <Box flexDirection="column" alignItems="center" justifyContent="center" height="100%">
      <Box alignSelf="stretch" background="bgLineHeavy" style={TAB_DIVIDER_STYLE} />
    </Box>
  );
}

const COUNT_ACTIVE_STYLE = { color: 'inherit', opacity: 1 } as const;
const COUNT_INACTIVE_STYLE = { color: 'inherit', opacity: 0.6 } as const;

function TabItem({ id, label, disabled, count }: CountedTabValue) {
  const { activeTab, updateActiveTab } = useTabsContext();
  const isActive = activeTab?.id === id;
  const handleClick = useCallback(() => updateActiveTab(id), [updateActiveTab, id]);
  return (
    <Pressable role="tab" aria-selected={isActive} onClick={handleClick} disabled={disabled}>
      <TabLabel id={id} active={isActive}>
        {count != null ? (
          <Box flexDirection="row" alignItems="center" gap={2}>
            {label}
            <Text
              as="span"
              font="body"
              style={isActive ? COUNT_ACTIVE_STYLE : COUNT_INACTIVE_STYLE}
            >
              {count}
            </Text>
          </Box>
        ) : (
          label
        )}
      </TabLabel>
    </Pressable>
  );
}

function ActiveIndicator(props: React.ComponentProps<typeof TabsActiveIndicator>) {
  return <TabsActiveIndicator {...props} background="bgPrimary" bottom={0} height={2} />;
}

export function TeamHeader({
  orgName,
  orgLogoSrc,
  memberCount,
  entities,
  allTab,
  categoryTabs,
  activeTab,
  onTabChange,
  onInvite,
  isInviteDisabled,
}: TeamHeaderProps) {
  const { formatMessage } = useIntl();

  const tabItems = useMemo(
    () => [
      ...(allTab != null
        ? [
            { id: allTab.id, label: allTab.id, count: allTab.count },
            ...(categoryTabs.length > 0
              ? [{ id: DIVIDER_ID, label: '', disabled: true, Component: TabDivider }]
              : []),
          ]
        : []),
      ...categoryTabs.map((tab) => ({ id: tab.id, label: tab.label ?? tab.id, count: tab.count })),
    ],
    [allTab, categoryTabs],
  );

  const activeTabItem = useMemo(
    () => tabItems.find((t) => t.id === activeTab) ?? null,
    [tabItems, activeTab],
  );

  const handleTabChange = useCallback(
    (tab: TabValue<string> | null) => {
      if (tab != null) onTabChange(tab.id);
    },
    [onTabChange],
  );

  const inviteButton = (
    <Button
      variant="primary"
      compact
      disabled={isInviteDisabled || onInvite == null}
      onClick={onInvite}
      data-dd-action-name="team_invite_open"
    >
      {formatMessage(messages.inviteTeamMember)}
    </Button>
  );

  return (
    <VStack gap={4} width="100%">
      {/* Title row */}
      <Box flexDirection="row" alignItems="center" justifyContent="space-between">
        <Box flexDirection="row" alignItems="center" gap={4}>
          {orgLogoSrc && (
            /* Raw <img> rather than CDS Avatar: Avatar's fixed size tokens don't map to
               --space-8, and its shape options don't include the rounded-rectangle the
               design calls for. Manual sizing and clipping give exact control. */
            <Box
              borderRadius={400}
              width="var(--space-8)"
              height="var(--space-8)"
              style={LOGO_BOX_STYLE}
            >
              <img
                src={orgLogoSrc}
                alt={formatMessage(messages.orgLogoAlt, { orgName })}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
          )}
          <VStack gap={0.25}>
            <Text as="h1" font="title1" data-testid="team-header-org-name">
              {orgName}
            </Text>
            <Box flexDirection="row" alignItems="center" gap={1}>
              <Text font="body" color="fgMuted" data-testid="team-header-member-count">
                {formatMessage(messages.memberCount, { count: memberCount })}
              </Text>
              {entities.length > 0 && (
                <>
                  <Text font="body" color="fgMuted">
                    ·
                  </Text>
                  <CountOrSingleCell
                    items={entities}
                    countMessage={messages.entities}
                    color="fgMuted"
                    font="body"
                  />
                </>
              )}
            </Box>
          </VStack>
        </Box>

        {isInviteDisabled ? (
          <Tooltip content={formatMessage(messages.inviteDisabledTooltip)}>
            {/* Box wrapper ensures the tooltip receives hover events on the disabled button */}
            <Box>{inviteButton}</Box>
          </Tooltip>
        ) : (
          inviteButton
        )}
      </Box>

      {/* Tabs row */}
      <Tabs
        gap={4}
        tabs={tabItems}
        activeTab={activeTabItem}
        onChange={handleTabChange}
        TabComponent={TabItem as TabComponent<string>}
        TabsActiveIndicatorComponent={ActiveIndicator}
      />
    </VStack>
  );
}
